import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ArrowLeft, Calendar as CalendarIcon, Save, FileText, Palette, Receipt } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Switch } from '../../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewGalleryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pricingSheets, setPricingSheets] = useState([]);
  const [backgroundCategories, setBackgroundCategories] = useState([]);
  const [form, setForm] = useState({
    name: '',
    description: '',
    event_type: '',
    event_date: null,
    expires_at: null,
    client_name: '',
    client_email: '',
    pricing_sheet_id: '',
    background_categories: [],
    tax_enabled: false,
    tax_rate: 5.0
  });

  useEffect(() => {
    fetchPricingSheets();
    fetchBackgroundCategories();
  }, []);

  const fetchPricingSheets = async () => {
    try {
      const response = await axios.get(`${API}/pricing-sheets`);
      setPricingSheets(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing sheets');
    }
  };

  const fetchBackgroundCategories = async () => {
    try {
      const response = await axios.get(`${API}/backgrounds/categories`);
      setBackgroundCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch background categories');
    }
  };

  const toggleBackgroundCategory = (category) => {
    setForm(prev => ({
      ...prev,
      background_categories: prev.background_categories.includes(category)
        ? prev.background_categories.filter(c => c !== category)
        : [...prev.background_categories, category]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.event_type) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...form,
        event_date: form.event_date ? format(form.event_date, 'yyyy-MM-dd') : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        pricing_sheet_id: form.pricing_sheet_id || null,
        background_categories: form.background_categories.length > 0 ? form.background_categories : null,
        tax_enabled: form.tax_enabled,
        tax_rate: form.tax_enabled ? parseFloat(form.tax_rate) : 5.0
      };
      
      const response = await axios.post(`${API}/galleries`, data);
      toast.success('Gallery created successfully');
      navigate(`/admin/galleries/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create gallery');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/galleries')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">New Gallery</h1>
          <p className="text-slate-600">Create a new photo gallery for your client</p>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading">Gallery Details</CardTitle>
          <CardDescription>Fill in the information about this gallery</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Gallery Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Smith Family - Soccer 2024"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="gallery-name-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description or notes"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="gallery-description-input"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Event Type *</Label>
                <Select 
                  value={form.event_type} 
                  onValueChange={(value) => setForm({ ...form, event_type: value })}
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sports">Sports</SelectItem>
                    <SelectItem value="school">School</SelectItem>
                    <SelectItem value="portrait">Portrait</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Event Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="event-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.event_date ? format(form.event_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.event_date}
                      onSelect={(date) => setForm({ ...form, event_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Gallery Expiration Date</Label>
              <p className="text-xs text-slate-500 mb-2">Optional: Orders will be disabled after this date</p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="expires-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.expires_at ? format(form.expires_at, 'PPP') : 'No expiration'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.expires_at}
                    onSelect={(date) => setForm({ ...form, expires_at: date })}
                    initialFocus
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
              {form.expires_at && (
                <Button 
                  type="button"
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setForm({ ...form, expires_at: null })}
                  className="text-xs"
                >
                  Clear expiration
                </Button>
              )}
            </div>

            {/* Pricing & Backgrounds Section */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Pricing & Backgrounds
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Pricing Sheet</Label>
                  <p className="text-xs text-slate-500">Select which products/packages to show in this gallery</p>
                  <Select 
                    value={form.pricing_sheet_id || "all"} 
                    onValueChange={(v) => setForm({ ...form, pricing_sheet_id: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="pricing-sheet-select">
                      <SelectValue placeholder="All products (no restriction)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All products (no restriction)</SelectItem>
                      {pricingSheets.map(sheet => (
                        <SelectItem key={sheet.id} value={sheet.id}>{sheet.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {backgroundCategories.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      Background Categories
                    </Label>
                    <p className="text-xs text-slate-500">Select which background categories to show (leave empty for all)</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border rounded-lg">
                      {backgroundCategories.map(category => (
                        <div key={category} className="flex items-center gap-2">
                          <Checkbox
                            id={`bg-cat-${category}`}
                            checked={form.background_categories.includes(category)}
                            onCheckedChange={() => toggleBackgroundCategory(category)}
                            data-testid={`bg-category-${category}`}
                          />
                          <label htmlFor={`bg-cat-${category}`} className="text-sm capitalize cursor-pointer">
                            {category}
                          </label>
                        </div>
                      ))}
                    </div>
                    {form.background_categories.length > 0 && (
                      <p className="text-xs text-blue-600">
                        {form.background_categories.length} categor{form.background_categories.length === 1 ? 'y' : 'ies'} selected
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-medium text-slate-900 mb-4">Client Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client_name">Client Name</Label>
                  <Input
                    id="client_name"
                    placeholder="John Smith"
                    value={form.client_name}
                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    data-testid="client-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_email">Client Email</Label>
                  <Input
                    id="client_email"
                    type="email"
                    placeholder="john@example.com"
                    value={form.client_email}
                    onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                    data-testid="client-email-input"
                  />
                </div>
              </div>
            </div>

            {/* Tax Settings */}
            <div className="border-t border-slate-200 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <Receipt className="h-5 w-5 text-green-600" />
                <h3 className="font-medium text-slate-900">Tax Settings</h3>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="tax_enabled" className="text-slate-800">Enable GST</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Charge GST (Goods and Services Tax) on orders from this gallery
                    </p>
                  </div>
                  <Switch
                    id="tax_enabled"
                    checked={form.tax_enabled}
                    onCheckedChange={(checked) => setForm({ ...form, tax_enabled: checked })}
                    data-testid="tax-enabled-switch"
                  />
                </div>
                
                {form.tax_enabled && (
                  <div className="space-y-2 pt-2 border-t border-green-200">
                    <Label htmlFor="tax_rate">GST Rate (%)</Label>
                    <Input
                      id="tax_rate"
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={form.tax_rate}
                      onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                      className="w-32"
                      data-testid="tax-rate-input"
                    />
                    <p className="text-xs text-green-700">
                      Standard GST rate in Canada is 5%
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/admin/galleries')}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="create-gallery-btn"
              >
                {loading ? (
                  'Creating...'
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Gallery
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewGalleryPage;
